// Reports.tsx
import React, { useMemo, useState, useRef, useCallback } from 'react';
import TabsLong from '../../components/TabsLong/TabsLong';
import { useSelector } from 'react-redux';
import styles from './styles/reports.module.css';
import RenderForm from '../../components/RenderForm/RenderForm';
import type { RenderFormHandle } from '../../components/RenderForm/RenderForm';
import {
  FaArrowLeftLong,
  FaCircleArrowLeft,
  FaCircleArrowRight,
} from 'react-icons/fa6';
import moment from 'moment';
import ReportTile from './ReportTile';
import { selectBinsSorted } from '../../Redux/slices/reportDataSlice';
import NoReportLayout from './NoReportLayout';

type NormalizedBin = {
  startMs: number;
  startISO: string;
  endExclusiveISO: string;
  reports: any[];
  byLayout?: Record<string, any[]>;
};

type WeekBucket = {
  weekStartISO: string;
  reports: any[];
};

type AppSection = 'REPORT' | 'INVENTORIES' | 'CHECKLISTS' | 'TRACKER';

interface ReportsInterface {
  type: AppSection;
  binsSelector?: (state: any) => NormalizedBin[] | undefined;
}

type TabsLongTabType =
  'INVENTORIES' | 'CHECKLISTS' | 'REPORTS' | 'TRACKERS' | 'SETUP';

const tabsTypeMap: Record<AppSection, TabsLongTabType> = {
  REPORT: 'REPORTS',
  INVENTORIES: 'INVENTORIES',
  CHECKLISTS: 'CHECKLISTS',
  TRACKER: 'TRACKERS',
};

// Stable empty array to avoid reallocations/flicker in deps
const EMPTY_BINS: ReadonlyArray<NormalizedBin> = Object.freeze([]);

const Reports: React.FC<ReportsInterface> = ({ type, binsSelector }) => {
  const formRef = useRef<RenderFormHandle>(null);

  const {
    inventoryTabItems,
    inventoryTab,
    reportsTabItems,
    reportsTab,
    checklistTab,
    checklistTabItems,
    trackerTabItems,
    trackerTab,
  } = useSelector((state: any) => state.tab);

  const { reportLayouts, inventoryLayouts, checklistLayouts, trackerLayouts } =
    useSelector((state: any) => state.reportLayouts);

  // --- Bins (memoize fallback to satisfy react-hooks/exhaustive-deps) ---
  const selectedBins = useSelector(binsSelector ?? selectBinsSorted);
  const bins: NormalizedBin[] = useMemo(
    () => (selectedBins ?? EMPTY_BINS) as NormalizedBin[],
    [selectedBins]
  );

  // --- Bindings per section ---
  const bindings = useMemo(() => {
    switch (type) {
      case 'INVENTORIES':
        return {
          tabIndex: inventoryTab,
          tabItems: inventoryTabItems,
          layouts: inventoryLayouts,
          tabsLongType: tabsTypeMap[type],
          headingPlural: 'Inventories',
          emptyLabel: 'inventories',
          newButtonLabel: 'New Inventory',
          nameProp: 'inventoryName' as const,
        };
      case 'CHECKLISTS':
        return {
          tabIndex: checklistTab,
          tabItems: checklistTabItems,
          layouts: checklistLayouts,
          tabsLongType: tabsTypeMap[type],
          headingPlural: 'Checklists',
          emptyLabel: 'checklists',
          newButtonLabel: 'New Checklist',
          nameProp: 'checklistName' as const,
        };
      case 'TRACKER':
        return {
          tabIndex: trackerTab,
          tabItems: trackerTabItems,
          layouts: trackerLayouts,
          tabsLongType: tabsTypeMap[type],
          headingPlural: 'Tracker',
          emptyLabel: 'tracker entries',
          newButtonLabel: 'New Tracker Entry',
          nameProp: 'trackerName' as const,
        };
      case 'REPORT':
      default:
        return {
          tabIndex: reportsTab,
          tabItems: reportsTabItems,
          layouts: reportLayouts,
          tabsLongType: tabsTypeMap['REPORT'],
          headingPlural: 'Reports',
          emptyLabel: 'reports',
          newButtonLabel: 'New Report',
          nameProp: 'reportName' as const,
        };
    }
  }, [
    type,
    reportsTab,
    reportsTabItems,
    reportLayouts,
    inventoryTab,
    inventoryTabItems,
    inventoryLayouts,
    checklistTab,
    checklistTabItems,
    checklistLayouts,
    trackerTab,
    trackerTabItems,
    trackerLayouts,
  ]);

  // Selected layout
  const layoutArray = useMemo(
    () => Object.values(bindings.layouts ?? {}) as any[],
    [bindings.layouts]
  );
  const selectedLayout = layoutArray[bindings.tabIndex];

  const layoutId: string | undefined = useMemo(() => {
    return (
      selectedLayout?.layoutId ??
      selectedLayout?._id ??
      Object.keys(bindings.layouts ?? {})[bindings.tabIndex]
    );
  }, [selectedLayout, bindings.layouts, bindings.tabIndex]);

  const serializedLayout = selectedLayout?.serializedLayout;

  const [showForm, setShowForm] = useState(false);

  // Time window
  const currentWeekISO = useMemo(
    () => moment().startOf('isoWeek').toISOString(),
    []
  );
  const [anchorISO, setAnchorISO] = useState<string>(currentWeekISO);

  const decreaseWeek = () =>
    setAnchorISO((prev) =>
      moment(prev).subtract(1, 'week').startOf('isoWeek').toISOString()
    );

  const addWeek = () =>
    setAnchorISO((prev) => {
      const next = moment(prev).add(1, 'week').startOf('isoWeek');
      const cur = moment(currentWeekISO);
      return next.isAfter(cur) ? currentWeekISO : next.toISOString();
    });

  const isAtCurrentWeek = anchorISO === currentWeekISO;

  const windowWeekStartsISO: string[] = useMemo(() => {
    const a = moment(anchorISO).startOf('isoWeek');
    return [
      a.clone().toISOString(),
      a.clone().subtract(1, 'week').toISOString(),
      a.clone().subtract(2, 'week').toISOString(),
      a.clone().subtract(3, 'week').toISOString(),
    ];
  }, [anchorISO]);

  const findBinForWeek = useCallback(
    (weekStartISO: string): NormalizedBin | undefined => {
      const t = new Date(weekStartISO).getTime();
      for (const b of bins) {
        const start = new Date(b.startISO).getTime();
        const end = new Date(b.endExclusiveISO).getTime();
        if (t >= start && t < end) return b;
      }
      return undefined;
    },
    [bins]
  );

  const getReportsForWeek = useCallback(
    (weekStartISO: string): any[] => {
      const weekStart = moment(weekStartISO);
      const weekEnd = weekStart.clone().add(1, 'week');
      const bin = findBinForWeek(weekStartISO);
      if (!bin) return [];

      const source = layoutId
        ? (bin.byLayout?.[String(layoutId)] ?? [])
        : (bin.reports ?? []);

      const out: any[] = [];
      for (const r of source) {
        const d = moment(r.submittedAt ?? r.createdAt ?? weekStartISO);
        if (d.isSameOrAfter(weekStart) && d.isBefore(weekEnd)) out.push(r);
      }
      out.sort(
        (a, b) =>
          moment(b.submittedAt ?? b.createdAt ?? 0).valueOf() -
          moment(a.submittedAt ?? a.createdAt ?? 0).valueOf()
      );
      return out;
    },
    [findBinForWeek, layoutId]
  );

  const weeklyBuckets: WeekBucket[] = useMemo(
    () =>
      windowWeekStartsISO.map((ws) => ({
        weekStartISO: ws,
        reports: getReportsForWeek(ws),
      })),
    [windowWeekStartsISO, getReportsForWeek]
  );

  // --- Renderers ---
  function renderList() {
    if (!bins.length) {
      return (
        <div className={styles.reportList}>
          No {bindings.emptyLabel} loaded.
        </div>
      );
    }

    return (
      <div className={styles.reportList}>
        {selectedLayout ? (
          weeklyBuckets.map((bucket) => (
            <div
              key={bucket.weekStartISO}
              className={styles.dateReportContainer}
            >
              <p className={styles.boxDate}>
                {`Week of ${moment(bucket.weekStartISO).format('MMMM D, YYYY')}`}
              </p>
              <div className={styles.dataDataContainer}>
                {bucket.reports.length === 0 ? (
                  <p className={styles.emptyState}>No {bindings.emptyLabel}</p>
                ) : (
                  bucket.reports.map((r: any, idx: number) => {
                    const dateStr = moment(
                      r.submittedAt ?? r.createdAt ?? bucket.weekStartISO
                    ).format('MMM D, YYYY');

                    // Prefer previewLabel when it's a non-empty string; otherwise use section-appropriate name field
                    const preview =
                      typeof r?.previewLabel === 'string'
                        ? r.previewLabel.trim()
                        : '';
                    const nameFromBinding = (r as any)?.[bindings.nameProp];
                    const displayName =
                      preview ||
                      nameFromBinding ||
                      r?.name ||
                      r?.title ||
                      r?.label ||
                      '(untitled)';

                    return (
                      <ReportTile
                        type={type}
                        key={r._id ?? `${bucket.weekStartISO}-${idx}`}
                        reportName={displayName}
                        date={dateStr}
                        reportId={r._id}
                      />
                    );
                  })
                )}
              </div>
            </div>
          ))
        ) : (
          <NoReportLayout type={type} />
        )}
      </div>
    );
  }

  function renderForm() {
    if (!selectedLayout) return <div>No Layouts Found</div>;
    const layoutName =
      selectedLayout?.[bindings.nameProp] ??
      selectedLayout?.name ??
      selectedLayout?.title ??
      layoutId ??
      '';
    return (
      <>
        <div className={styles.formHeadlineContainer}>
          <p className={styles.formHeadline}>{`New ${layoutName}`}</p>
        </div>
        <RenderForm
          ref={formRef}
          key={String(layoutId ?? 'current-layout')}
          serializedData={serializedLayout}
          resetView={() => setShowForm(false)}
          type={type}
        />
      </>
    );
  }

  return (
    <div>
      {/* TabsLong expects plural literals, so map with tabsTypeMap */}
      <TabsLong tabType={tabsTypeMap[type]} />

      <div className={styles.headerContainer}>
        {showForm ? (
          <div className={styles.arrowContainer}>
            <FaArrowLeftLong
              className={styles.arrowLeft}
              onClick={() => setShowForm(false)}
            />
            <button
              className={styles.submitButton}
              onClick={() => formRef.current?.submit()}
            >
              Submit
            </button>
          </div>
        ) : (
          selectedLayout && (
            <button
              className={styles.newReportButton}
              onClick={() => setShowForm(true)}
            >
              {
                {
                  REPORT: 'New Report',
                  INVENTORIES: 'New Inventory',
                  CHECKLISTS: 'New Checklist',
                  TRACKER: 'New Tracker Entry',
                }[type]
              }
            </button>
          )
        )}

        {!showForm && (
          <div
            style={selectedLayout ? {} : { width: '97%' }}
            className={styles.pager}
          >
            {selectedLayout && (
              <FaCircleArrowLeft
                className={styles.arrowActive}
                onClick={decreaseWeek}
              />
            )}
            <p className={styles.headline}>
              {
                {
                  REPORT: 'Reports',
                  INVENTORIES: 'Inventories',
                  CHECKLISTS: 'Checklists',
                  TRACKER: 'Tracker',
                }[type]
              }
            </p>
            {selectedLayout && (
              <FaCircleArrowRight
                className={
                  isAtCurrentWeek ? styles.arrowInactive : styles.arrowActive
                }
                onClick={!isAtCurrentWeek ? addWeek : undefined}
              />
            )}
          </div>
        )}
      </div>

      {showForm ? renderForm() : renderList()}
    </div>
  );
};

export default Reports;
